import React, { useCallback, useState } from 'react';
import { DndSortableElementId } from './types';
import { Props as DndSortableElementProps } from './DndSortableElement';

export interface Props {
  // Called when an element with `id` is dragged over `position`
  onDragOver?: (id: DndSortableElementId, position: number, initialPosition: number) => void;
  // Called when a drag is finished for element with `id`
  onDragEnd?: (id: DndSortableElementId, initialPosition: number) => void;
  // Called when an element with `id` is dropped onto `position`
  onDrop?: (id: DndSortableElementId, position: number) => void;
  children: (props: DndSortableChildrenProps) => any;
}

export interface DndSortableChildrenProps extends Pick<
  DndSortableElementProps,
  'onDragBegin' | 'onDragEnd' | 'onHover' | 'onDrop'
> {
  draggedElementId: DndSortableElementId | null;
}

const DndSortable: React.FC<Props> = ({ onDragOver, onDragEnd, onDrop, children }) => {
  const [draggedElement, setDraggedElement] = useState<{
    id: DndSortableElementId;
    initialPosition: number;
  } | null>(null);

  const onDragBegin = useCallback((id: DndSortableElementId, position: number) => {
    setDraggedElement({ id, initialPosition: position });
  }, []);

  const onDragEndHandler = () => {
    if (draggedElement && onDragEnd) {
      onDragEnd(draggedElement.id, draggedElement.initialPosition);
    }
    setDraggedElement(null);
  };

  const onDropHandler = useCallback(
    (position: number) => {
      if (
        draggedElement !== null &&
        position !== null &&
        draggedElement.initialPosition !== position
      ) {
        onDrop && onDrop(draggedElement.id, position);
      }
    },
    [draggedElement, onDrop]
  );

  const onHoverHandler = useCallback(
    (position: number) => {
      if (draggedElement !== null) {
        onDragOver && onDragOver(draggedElement.id, position, draggedElement.initialPosition);
      }
    },
    [draggedElement, onDragOver]
  );

  return children({
    onDragBegin,
    onDragEnd: onDragEndHandler,
    onHover: onHoverHandler,
    onDrop: onDropHandler,
    draggedElementId: draggedElement ? draggedElement.id : null,
  });
};

DndSortable.displayName = 'DndSortable';

export default DndSortable;
